import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { RootState, AppDispatch } from '../store';
import { fetchFirmStatistics, fetchGlobalStatistics } from '../store/slices/firmsSlice';
import { fetchCases } from '../store/slices/casesSlice';
import { fetchClients } from '../store/slices/clientsSlice';
import { fetchBills } from '../store/slices/billingSlice';
import { fetchMonthlyRevenue } from '../store/slices/billingSlice';
import { fetchTasks } from '../store/slices/tasksSlice';
import {
  FirmAdminView,
  LawyerView,
  ClientView,
  StaffView,
  DefaultUserView
} from '../components/Dashboard/views';
import {
  isFirmAdminStats,
  isLawyerStats,
  isClientStats,
  isStaffStats,
  isDefaultUserStats
} from '../types/statistics';
import StatusBadge from '../components/common/StatusBadge';
import { getCaseProgressFromStatus } from '../utils/caseProgress';

interface RecentCase {
  id: number;
  case_number: string;
  title: string;
  client_name: string;
  status: string;
  progress: number;
  created_at: string;
}

const Dashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { statistics: firmsStats, statisticsResponse } = useSelector((state: RootState) => state.firms);
  const { cases: casesData } = useSelector((state: RootState) => state.cases);
  const { clients } = useSelector((state: RootState) => state.clients);
  const { bills } = useSelector((state: RootState) => state.bills);
  const { tasks } = useSelector((state: RootState) => state.tasks);
  const { monthlyRevenue } = useSelector((state: RootState) => state.revenue);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recentCases, setRecentCases] = useState<RecentCase[]>([]);

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!isAuthenticated || !user) return;

      setLoading(true);
      setError(null);

      try {
        // Load data based on user role
        if (user.role?.name === 'super-admin') {
          await dispatch(fetchGlobalStatistics());
        } else {
          await dispatch(fetchFirmStatistics(user?.firm_id!));
          await Promise.all([
            dispatch(fetchCases({ userId: user.user_id, page: 1, limit: 5 })),
            dispatch(fetchClients(user.user_id)),
            dispatch(fetchBills(user.user_id)),
            dispatch(fetchTasks(user.user_id)),
            dispatch(fetchMonthlyRevenue({ userId: user.user_id })),
          ]);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard data');
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [dispatch, isAuthenticated, user]);

  // Update recent cases when Redux cases data changes
  useEffect(() => {
    if (Array.isArray(casesData)) {
      const cases = casesData?.map((caseItem: any) => {
        const client = clients.find((c) => c.id === caseItem.customer_id);
        return {
          id: caseItem.id,
          case_number: caseItem.case_number || `#${caseItem.id}`,
          title: caseItem.subject || 'Untitled Case',
          client_name: client?.company || caseItem.client_name || 'Unknown Client',
          status: caseItem.status || 'pending',
          progress: getCaseProgressFromStatus(caseItem.status),
          created_at: caseItem.created_at || new Date().toISOString(),
        };
      });
      setRecentCases(cases);
    }
  }, [casesData, clients]);

  const overdueTasks = (tasks || []).filter((task: any) => {
    if (!task.due_date || task.status === 'completed') return false;
    return new Date(task.due_date).getTime() < Date.now();
  });

  const openTasksCount = (tasks || []).filter((task: any) => task.status !== 'completed').length;
  const activeClientsCount = clients?.length || 0;
  const documentsCount = (casesData || []).reduce(
    (sum: number, c: any) => sum + (Array.isArray(c.files) ? c.files.length : 0),
    0,
  );

  const recentActivity = [
    ...(casesData || []).map((c: any) => ({
      id: `case-${c.id}`,
      label: c.subject || 'Untitled Case',
      status: c.status,
      date: c.updated_at || c.created_at,
    })),
    ...(tasks || []).map((task: any) => ({
      id: `task-${task.id}`,
      label: task.title,
      status: task.status,
      date: task.updated_at || task.created_at,
    })),
  ]
    .filter((item) => item.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 5);

  const getWelcomeMessage = () => {
    if (!user) return t('dashboard.welcome');
    
    const firstName = user.full_name?.split(' ')[0] || user.full_name || 'User';
    return t('dashboard.welcomeBack', { name: firstName });
  };

  const getRoleDescription = () => {
    if (!user) return '';
    
    const descriptions = {
      'super-admin': t('dashboard.manageAllFirms'),
      admin: t('dashboard.manageFirm'),
      lawyer: t('dashboard.manageCases'),
      client: t('dashboard.trackCases'),
      staff: t('dashboard.supportTeam'),
    };
    
    return descriptions[user.role?.name as keyof typeof descriptions] || '';
  };

  const renderRoleBasedView = () => {
    if (!firmsStats) return null;

    if (isFirmAdminStats(firmsStats)) {
      return <FirmAdminView data={firmsStats} />;
    }
    
    if (isLawyerStats(firmsStats)) {
      return <LawyerView data={firmsStats} />;
    }
    
    if (isClientStats(firmsStats)) {
      return <ClientView data={firmsStats} />;
    }
    
    if (isStaffStats(firmsStats)) {
      return <StaffView data={firmsStats} />;
    }
    
    if (isDefaultUserStats(firmsStats)) {
      return <DefaultUserView data={firmsStats} />;
    }

    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>{t('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 4,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 3,
          color: '#ffffff',
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          boxShadow: '0px 8px 24px rgba(37, 99, 235, 0.25)',
        }}
      >
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600, color: '#ffffff' }}>
          {getWelcomeMessage()}
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
          {getRoleDescription()}
        </Typography>
        {statisticsResponse && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`Role: ${statisticsResponse.user_role}`}
              size="small"
              sx={{
                color: '#ffffff',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                bgcolor: 'rgba(255, 255, 255, 0.12)',
              }}
              variant="outlined"
            />
            <Chip
              label={`Access: ${statisticsResponse.access_level}`}
              size="small"
              sx={{
                color: '#ffffff',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                bgcolor: 'rgba(255, 255, 255, 0.12)',
              }}
              variant="outlined"
            />
            <Chip
              label={`Plan: ${firmsStats?.subscription_plan}`}
              size="small"
              color={firmsStats?.subscription_status === 'active' ? 'success' : 'error'}
              sx={{ fontWeight: 600 }}
            />
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Role-based statistics view */}
      {renderRoleBasedView()}

      {/* Recent Cases - Only show for non-super-admin users */}
      {user?.role?.name !== 'super-admin' && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {user?.role?.name === 'client' ? t('dashboard.myCases') : t('dashboard.recentCases')}
          </Typography>

          {recentCases.length > 0 ? (
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                {recentCases?.map((caseItem, index) => (
                  <Box
                    key={caseItem.id}
                    sx={{
                      p: 2,
                      mb: index === recentCases.length - 1 ? 0 : 1,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'background-color 0.15s ease, border-color 0.15s ease',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderColor: 'primary.light',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {caseItem.case_number}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StatusBadge status={caseItem.status} />
                        <Typography variant="caption" color="text.secondary" component="span">
                          {new Date(caseItem.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} component="span">
                      {caseItem.title} - {caseItem.client_name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', maxWidth: 220 }}>
                        <Typography variant="caption" color="text.secondary" component="span">
                          {t('dashboard.progress')}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={caseItem.progress}
                          sx={{ flex: 1, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {caseItem.progress}%
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <Typography variant="body2">{t('dashboard.noCasesAvailable')}</Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {user?.role?.name !== 'super-admin' && (
        <>
          {/* Secondary stats row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 3, mx: -1 }}>
            {[
              { label: t('navigation.bills'), value: bills?.length || 0 },
              { label: t('dashboard.openTasks', 'Open Tasks'), value: openTasksCount },
              { label: t('reports.activeClients'), value: activeClientsCount },
              { label: t('dashboard.documents'), value: documentsCount },
            ].map((stat) => (
              <Box key={stat.label} sx={{ width: { xs: '50%', sm: '25%' }, p: 1 }}>
                <Card>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                      {stat.label}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {stat.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1, mx: -1.5 }}>
            {/* Overdue actions */}
            <Box sx={{ width: { xs: '100%', md: '50%' }, p: 1.5 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('dashboard.overdueActions', 'Overdue Actions')}
                  </Typography>
                  {overdueTasks.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.noOverdueActions', 'No overdue actions')}
                    </Typography>
                  ) : (
                    overdueTasks.slice(0, 5).map((task: any) => (
                      <Box
                        key={task.id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' },
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {task.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('dashboard.due', 'Due')} {new Date(task.due_date).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <StatusBadge status={task.status} />
                      </Box>
                    ))
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* Monthly revenue chart */}
            <Box sx={{ width: { xs: '100%', md: '50%' }, p: 1.5 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('dashboard.monthlyRevenue', 'Monthly Revenue')}
                  </Typography>
                  {monthlyRevenue && monthlyRevenue.length > 0 ? (
                    <BarChart
                      height={220}
                      xAxis={[{
                        scaleType: 'band',
                        data: monthlyRevenue.map((m: any) =>
                          new Date(2000, (m.month || 1) - 1).toLocaleString(undefined, { month: 'short' }),
                        ),
                      }]}
                      series={[{ data: monthlyRevenue.map((m: any) => parseFloat(m.collected) || 0), color: '#2563eb' }]}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.noRevenueData', 'No revenue data yet')}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Recent activity */}
          <Box sx={{ mt: 1 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('dashboard.recentActivity', 'Recent Activity')}
                </Typography>
                {recentActivity.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.noRecentActivity', 'No recent activity')}
                  </Typography>
                ) : (
                  recentActivity.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <Typography variant="body2">{item.label}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <StatusBadge status={item.status} />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(item.date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </CardContent>
            </Card>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
